import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { Bean, NumberPatternType, ValidationRules } from '@dreryk/react-common';
import { NumberInput } from '@dreryk/react-components';

import { getQuantityMaxValue, getQuantityMinValue, getQuantityPrecision } from '~libs.shared.prescription';

import {
   DraftPrescriptionFieldName,
   DraftPrescriptionsFormController,
} from '../../draftPrescriptions/DraftPrescriptionsForm.types';
import { prescriptionTranslationKeys } from '../../../../../translations';

import { Quantity } from './quantity.types';

type QuantityInputProps = Pick<DraftPrescriptionsFormController, 'register' | 'trigger'> & {
   readonly name: DraftPrescriptionFieldName;
   readonly defaultValue: Quantity | null;
   readonly dosageExceedingPackages: boolean;
   readonly disabled?: boolean;
};

export const QuantityInput = (props: QuantityInputProps): JSX.Element => {
   const handleBlur = (): void => {
      props.trigger(props.name);
   };

   useUpdateEffect(() => {
      props.trigger(props.name);
   }, [props.dosageExceedingPackages, props.trigger, props.name]);

   const validationRules = useMemo<ValidationRules<Bean>>(
      () => ({
         required: true,
         max: getQuantityMaxValue(),
         validate: () => !props.dosageExceedingPackages,
      }),
      [props.dosageExceedingPackages]
   );

   return (
      <NumberInput
         controller={props.register(props.name, validationRules)}
         defaultValue={props.defaultValue || undefined}
         label={prescriptionTranslationKeys.quantity.quantity}
         min={getQuantityMinValue()}
         max={getQuantityMaxValue()}
         onBlur={handleBlur}
         disabled={props.disabled}
         autoComplete={false}
         withoutRequiredMark
         labelNoWrap
         compact
         testId={'quantity'}
         pattern={NumberPatternType.POSITIVE_FLOAT}
         precision={getQuantityPrecision()}
      />
   );
};
